<template>
  <canvas ref="chartCanvas" height="400"></canvas>
</template>

<script>
import { ref, onMounted, watch, onBeforeUnmount, computed } from 'vue';
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

export default {
  name: 'BaseBarChart',
  props: {
    chartData: {
      type: Object,
      required: true
    },
  },
  setup(props) {
    const chartCanvas = ref(null);
    const chartInstance = ref(null);

    // 固定カラーパレット
    const predefinedBackgroundColors = [
      'rgba(255, 99, 132, 0.2)',  // Red
      'rgba(54, 162, 235, 0.2)',  // Blue
      'rgba(255, 206, 86, 0.2)',  // Yellow
      'rgba(75, 192, 192, 0.2)',  // Green
      'rgba(153, 102, 255, 0.2)', // Purple
      'rgba(255, 159, 64, 0.2)',  // Orange
      'rgba(201, 203, 207, 0.2)', // Gray
      'rgba(255, 99, 71, 0.2)',   // Tomato
      'rgba(60, 179, 113, 0.2)',  // Medium Sea Green
      'rgba(123, 104, 238, 0.2)', // Medium Slate Blue
      'rgba(255, 165, 0, 0.2)',   // Orange
      'rgba(255, 20, 147, 0.2)'    // Deep Pink
    ];

    const predefinedBorderColors = [
      'rgba(255, 99, 132, 1)',    // Red
      'rgba(54, 162, 235, 1)',    // Blue
      'rgba(255, 206, 86, 1)',    // Yellow
      'rgba(75, 192, 192, 1)',    // Green
      'rgba(153, 102, 255, 1)',   // Purple
      'rgba(255, 159, 64, 1)',    // Orange
      'rgba(201, 203, 207, 1)',   // Gray
      'rgba(255, 99, 71, 1)',     // Tomato
      'rgba(60, 179, 113, 1)',    // Medium Sea Green
      'rgba(123, 104, 238, 1)',   // Medium Slate Blue
      'rgba(255, 165, 0, 1)',     // Orange
      'rgba(255, 20, 147, 1)'      // Deep Pink
    ];

    // 色の割り当てロジック
    const computedChartData = computed(() => {
      const labels = props.chartData.labels || [];
      const data = props.chartData.datasets[0].data || [];
      const numData = data.length;

      // 背景色と枠線色を割り当て
      const backgroundColors = data.map((_, index) => predefinedBackgroundColors[index % predefinedBackgroundColors.length]);
      const borderColors = data.map((_, index) => predefinedBorderColors[index % predefinedBorderColors.length]);

      return {
        labels,
        datasets: [
          {
            label: props.chartData.datasets[0].label || '',
            data,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 1
          }
        ]
      };
    });

    onMounted(() => {
      Chart.register(
        BarController,
        BarElement,
        CategoryScale,
        LinearScale,
        Title,
        Tooltip,
        Legend,
        ChartDataLabels
      );

      const ctx = chartCanvas.value.getContext('2d');

      const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: props.chartData.titleText,
            font: {
              size: 18,
            },
          },
          legend: {
            display: false,
          },
          datalabels: {
            formatter: (value) => {
              return value.toLocaleString();
            },
            font: {
              weight: 'bold',
              size: 14
            }
          },
        },
        scales: {
          y: {
            beginAtZero: true,
          },
          x: {
            ticks: {
              font: {
                size: 16,
              },
            },
          },
        },
      }
      
      chartInstance.value = new Chart(ctx, {
        type: 'bar',
        data: computedChartData.value,
        options: chartOptions
      });
    });

    onBeforeUnmount(() => {
      if (chartInstance.value) {
        chartInstance.value.destroy();
      }
    });

    return {
      chartCanvas
    };
  }
};
</script>

<style scoped>
/* 必要に応じてスタイルを追加 */
</style>
